// src/components/admin/RevenueWidget.js
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  Box,
  Avatar,
  LinearProgress,
  Chip,
  Divider,
  Paper,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  EventSeat as RegistrationIcon,
  CreditCard as SubscriptionIcon,
  AccountBalance as BankIcon,
  PieChart as PieChartIcon,
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

const RevenueWidget = ({ data }) => {
  const [tabValue, setTabValue] = useState(0);

  if (!data) return null;

  const revenueStreams = [
    {
      name: 'Inscripciones',
      amount: data.registrations || 0,
      percentage: ((data.registrations / data.total) * 100).toFixed(1),
      growth: '+23%',
      color: 'primary',
      icon: <RegistrationIcon />
    },
    {
      name: 'Suscripciones',
      amount: data.subscriptions || 0,
      percentage: ((data.subscriptions / data.total) * 100).toFixed(1),
      growth: '+15%',
      color: 'success',
      icon: <SubscriptionIcon />
    }
  ];

  const monthlyData = [
    { month: 'Nov', registrations: 18500, subscriptions: 12300, total: 30800 },
    { month: 'Dic', registrations: 23450, subscriptions: 15600, total: 39050 },
    { month: 'Ene', registrations: 28900, subscriptions: 18200, total: 47100 }
  ];

  const recentTransactions = [
    {
      id: 1,
      type: 'subscription',
      description: 'Suscripción Premium - Organizador Pro',
      amount: 299,
      date: '2024-12-09 14:30',
      status: 'completed'
    },
    {
      id: 2,
      type: 'registration',
      description: 'Inscripción - Maratón Ciudad 2024',
      amount: 150,
      date: '2024-12-09 13:45',
      status: 'completed'
    },
    {
      id: 3,
      type: 'registration',
      description: 'Inscripción - Copa Juvenil Natación',
      amount: 75,
      date: '2024-12-09 12:20',
      status: 'completed'
    },
    {
      id: 4,
      type: 'subscription',
      description: 'Suscripción Básica - Organizador',
      amount: 99,
      date: '2024-12-09 11:15',
      status: 'pending'
    }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getTransactionIcon = (type) => {
    return type === 'subscription' ? <SubscriptionIcon /> : <RegistrationIcon />;
  };

  const getTransactionColor = (type) => {
    return type === 'subscription' ? 'success' : 'primary';
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const exportRevenue = () => {
    console.log('Exportando reporte de ingresos...');
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader
        title="💰 Ingresos y Ganancias"
        subheader={`Total: ${formatCurrency(data.total)}`}
        action={
          <Box display="flex" gap={1}>
            <Tooltip title="Exportar reporte">
              <IconButton size="small" onClick={exportRevenue}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Actualizar datos">
              <IconButton size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Chip 
              label={`+${data.growth}%`} 
              color="success" 
              size="small"
              icon={<TrendingUpIcon />}
            />
          </Box>
        }
      />
      
      <CardContent>
        {/* Tabs para diferentes vistas */}
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label="Resumen" />
          <Tab label="Tendencias" />
          <Tab label="Transacciones" />
        </Tabs>

        {/* Tab 1: Resumen de ingresos */}
        {tabValue === 0 && (
          <Box>
            {/* Ingresos totales destacados */}
            <Paper 
              sx={{ 
                p: 3, 
                mb: 3, 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                textAlign: 'center'
              }}
            >
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                {formatCurrency(data.total)}
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9 }}>
                Ingresos Totales Acumulados
              </Typography>
              <Box display="flex" justifyContent="center" alignItems="center" gap={1} mt={1}>
                <TrendingUpIcon />
                <Typography variant="body2">
                  +{data.growth}% vs mes anterior
                </Typography>
              </Box>
            </Paper>

            {/* Desglose por fuente de ingresos */}
            <Typography variant="subtitle2" gutterBottom>
              📊 Fuentes de Ingresos
            </Typography>
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {revenueStreams.map((stream, index) => (
                <Grid item xs={6} key={index}>
                  <Paper sx={{ p: 2, height: '100%', border: 1, borderColor: 'divider' }}>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Avatar sx={{ bgcolor: `${stream.color}.main` }}>
                        {stream.icon}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {stream.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {stream.percentage}% del total
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Typography variant="h5" fontWeight="bold" color={`${stream.color}.main`}>
                      {formatCurrency(stream.amount)}
                    </Typography>
                    
                    <Box sx={{ mt: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={parseFloat(stream.percentage)}
                        color={stream.color}
                        sx={{ height: 6, borderRadius: 3, mb: 1 }}
                      />
                      <Chip
                        label={stream.growth}
                        color="success"
                        size="small"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Métricas mensuales */}
            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                📈 Este Mes vs Anterior
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Box textAlign="center">
                    <Typography variant="h6" color="primary.main">
                      {formatCurrency(data.thisMonth)}
                    </Typography>
                    <Typography variant="caption">Este Mes</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box textAlign="center">
                    <Typography variant="h6" color="text.secondary">
                      {formatCurrency(data.lastMonth)}
                    </Typography>
                    <Typography variant="caption">Mes Anterior</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box textAlign="center">
                    <Typography variant="h6" color="success.main">
                      +{data.growth}%
                    </Typography>
                    <Typography variant="caption">Crecimiento</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        )}

        {/* Tab 2: Tendencias mensuales */}
        {tabValue === 1 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              📊 Evolución Mensual (últimos 3 meses)
            </Typography>
            
            {monthlyData.map((month, index) => (
              <Paper key={index} sx={{ p: 2, mb: 2, border: 1, borderColor: 'divider' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="h6" fontWeight="bold">
                    {month.month} 2024
                  </Typography>
                  <Typography variant="h6" color="primary.main">
                    {formatCurrency(month.total)}
                  </Typography>
                </Box>
                
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Inscripciones
                      </Typography>
                      <Typography variant="h6" color="primary.main">
                        {formatCurrency(month.registrations)}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={(month.registrations / month.total) * 100}
                        color="primary"
                        sx={{ height: 4, borderRadius: 2 }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Suscripciones
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        {formatCurrency(month.subscriptions)}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={(month.subscriptions / month.total) * 100}
                        color="success"
                        sx={{ height: 4, borderRadius: 2 }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            ))}

            {/* Proyección */}
            <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 2 }}>
              <Typography variant="subtitle2" color="info.dark" gutterBottom>
                🔮 Proyección para Enero 2025
              </Typography>
              <Typography variant="body2" color="info.dark">
                Basado en la tendencia actual: {formatCurrency(47100 * 1.18)} (+18% estimado)
              </Typography>
            </Box>
          </Box>
        )}

        {/* Tab 3: Transacciones recientes */}
        {tabValue === 2 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              💳 Transacciones Recientes
            </Typography>
            
            <List dense>
              {recentTransactions.map((transaction) => (
                <ListItem key={transaction.id} divider>
                  <ListItemIcon>
                    <Avatar 
                      sx={{ 
                        bgcolor: `${getTransactionColor(transaction.type)}.main`,
                        width: 32,
                        height: 32
                      }}
                    >
                      {getTransactionIcon(transaction.type)}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" fontWeight="bold">
                          {transaction.description}
                        </Typography>
                        <Typography variant="body2" color="primary.main" fontWeight="bold">
                          {formatCurrency(transaction.amount)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                          {new Date(transaction.date).toLocaleString('es-ES')}
                        </Typography>
                        <Chip
                          label={transaction.status === 'completed' ? 'Completado' : 'Pendiente'}
                          color={transaction.status === 'completed' ? 'success' : 'warning'}
                          size="small"
                        />
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>

            {/* Resumen de transacciones */}
            <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                💼 Resumen del día: {recentTransactions.length} transacciones • {' '}
                {formatCurrency(recentTransactions.reduce((sum, t) => sum + t.amount, 0))} en ingresos • {' '}
                {recentTransactions.filter(t => t.status === 'completed').length} completadas
              </Typography>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default RevenueWidget;